/**
 * Online match flow, menu side (light: no sim, no netcode — part of the first-paint bundle). OnlineClient.onMatch →
 * keep the handle (by matchId) → `ui.matchSetup` with the online info → Versus splash (the UI calls startMatch after
 * ~3 s; a fallback starts it if it does not) → server verdict merged into `ui.results` → rematch on the same transport.
 * The match itself (RollbackSession, confirmed timeline) is `online.ts`, loaded with the match scene.
 */
import type { GameMode, MatchConfig, PlayerIndex } from '@hf/shared';
import { DEVICE_ID } from '../input/types';
import type { OnlineClient, OnlineMatchHandle, MatchFound, MatchResultMessage } from '../net/types';
import type { UiStore } from '../ui-model/store';
import type { MatchSetup, SlotSetup } from '../ui-model/types';

/** Online game modes (GameMode values that run a RollbackSession). */
export const isOnlineMode = (mode: GameMode): boolean =>
  mode === 'online_quick' || mode === 'online_ranked' || mode === 'private';

/** How long the Versus splash may stay up before the match starts anyway (the UI normally starts it at ~3 s). */
export const VERSUS_FALLBACK_MS = 6_000;
/** MatchSetup for an online match from the server's match_found. */
export const setupFromFound = (found: MatchFound): MatchSetup => {
  const you = found.you;
  const config: MatchConfig = {
    seed: found.config.seed,
    format: found.config.format,
    characters: [found.config.characters[0], found.config.characters[1]],
    normalizedStats: found.config.normalizedStats,
    specials: found.config.specials,
    drivesPerPlayer: found.config.drivesPerPlayer,
    firstPossession: found.config.firstPossession,
    wind: found.config.wind,
    ...(found.config.timeOfDay ? { timeOfDay: found.config.timeOfDay } : {}),
  };
  const slot = (i: PlayerIndex): SlotSetup => {
    const p = found.players[i];
    return i === you
      ? {
          kind: 'human',
          name: p.name,
          character: p.character,
          // The only human: every connected pad drives the slot (not just index 0).
          deviceIds: [DEVICE_ID.KB_SOLO, DEVICE_ID.ANY_PAD, DEVICE_ID.TOUCH],
        }
      : { kind: 'remote', name: p.name, character: p.character };
  };
  const mode: GameMode =
    found.kind === 'ranked' ? 'online_ranked' : found.kind === 'private' ? 'private' : 'online_quick';
  return {
    mode,
    config,
    slots: [slot(0), slot(1)],
    online: {
      matchId: found.matchId,
      kind: found.kind,
      you,
      opponent: { id: found.players[you === 0 ? 1 : 0].id, name: found.players[you === 0 ? 1 : 0].name },
    },
  };
};

const signed = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `\u2212${-n}` : '\u00b10');

/** A server verdict that arrives without a Results screen (forfeit while the match was still on). */
export const verdictToast = (
  m: MatchResultMessage,
  you: PlayerIndex,
): { text: string; kind: 'success' | 'info' } | null => {
  if (m.status === 'disputed') return null;
  const rating = m.rating ? ` (${signed(m.rating.delta)} rating)` : '';
  if (m.winner === you)
    return {
      text: m.status === 'forfeit' ? `Opponent left: you win${rating}` : `You win${rating}`,
      kind: 'success',
    };
  if (m.winner === -1) return null;
  return { text: `${m.status === 'forfeit' ? 'Forfeit: you lose' : 'You lose'}${rating}`, kind: 'info' };
};

/**
 * Copy for a 'disputed' verdict. With a Results screen both peers finished and reported different results; without
 * one the game never finished (a connection loss, a desync or the server's time limit): a no-contest.
 */
export const disputedToast = (hadResults: boolean, desynced: boolean): string =>
  hadResults
    ? 'Results did not match: no rating change'
    : desynced
      ? 'Out of sync: no contest, no rating change'
      : 'Connection lost: no contest, no rating change';

export interface OnlineFlowNav {
  goto(screen: 'versus' | 'main_menu' | 'online_lobby'): void;
  startMatch(setup: MatchSetup): void;
}

/** App-lifetime online orchestration (one instance, created at boot). */
export class OnlineFlow {
  private readonly handles = new Map<string, OnlineMatchHandle>();
  private current: OnlineMatchHandle | null = null;
  private resultSeen = false;
  /** We withdrew the rematch offer ourselves (left Results): the server's "declined" echo is not news. */
  private declinedLocally = false;
  private readonly unsubs: (() => void)[] = [];
  private handleUnsubs: (() => void)[] = [];
  private fallback: ReturnType<typeof setTimeout> | null = null;
  /**
   * Why the last opponent left according to the server (`opponent_left.reason`: quit / disconnect / timeout…), null
   * until one did. Diagnostics: the e2e failure snapshot tells a server-side leave from a peer-link timeout.
   */
  lastOpponentLeft: string | null = null;

  constructor(
    private readonly ui: UiStore,
    private readonly online: OnlineClient,
    private readonly nav: OnlineFlowNav,
  ) {
    this.unsubs.push(online.onMatch((h) => this.onMatch(h)));
  }

  /** The latest handle of a match (rematches replace it). */
  handleFor(matchId: string): OnlineMatchHandle | null {
    const h = this.handles.get(matchId);
    return h && !h.closed ? h : null;
  }

  get active(): OnlineMatchHandle | null {
    return this.current && !this.current.closed ? this.current : null;
  }

  private onMatch(h: OnlineMatchHandle): void {
    for (const u of this.handleUnsubs.splice(0)) u();
    this.handles.clear();
    this.handles.set(h.found.matchId, h);
    this.current = h;
    this.resultSeen = false;
    this.declinedLocally = false;
    this.handleUnsubs.push(
      h.onResult((m) => {
        this.resultSeen = true;
        const r = this.ui.results.value;
        if (r) {
          this.ui.results.value = {
            ...r,
            status: m.status,
            ...(m.rating ? { ratingChange: m.rating } : {}),
          };
        }
        if (m.status === 'disputed')
          this.ui.toast(
            disputedToast(r !== null, this.ui.overlay.value === 'desynced'),
            r ? 'error' : 'info',
          );
        else if (!r) {
          // No Results screen for this game (the opponent left mid-match → forfeit): still tell the player.
          const toast = verdictToast(m, h.found.you);
          if (toast) this.ui.toast(toast.text, toast.kind);
        }
      }),
      h.onRematchState((m) => {
        if (m.declined) {
          if (!this.declinedLocally) this.ui.toast('Rematch declined', 'info');
        } else if (m.opponent && !m.you) this.ui.toast('Your opponent wants a rematch!', 'success');
        else if (m.you && !m.opponent) this.ui.toast('Rematch offered — waiting for your opponent…', 'info');
      }),
      h.onOpponentLeft((m) => {
        this.lastOpponentLeft = m.reason;
        if (this.ui.screen.value === 'match') {
          if (this.ui.overlay.value === 'none' || this.ui.overlay.value === 'pause')
            this.ui.overlay.value = 'disconnected';
        } else if (this.ui.screen.value === 'versus') {
          this.ui.toast('Your opponent left before kickoff', 'error');
          this.nav.goto('online_lobby');
        } else
          this.ui.toast(m.reason === 'quit' ? 'Your opponent left' : 'Your opponent disconnected', 'info');
      }),
    );
    const setup = setupFromFound(h.found);
    this.ui.matchSetup.value = setup;
    // A new game never inherits a Results view (a stale one would swallow this game's forfeit verdict toast).
    this.ui.results.value = null;
    this.nav.goto('versus');
    if (this.fallback !== null) clearTimeout(this.fallback);
    this.fallback = setTimeout(() => {
      this.fallback = null;
      if (this.ui.screen.value === 'versus' && this.ui.matchSetup.value === setup) this.nav.startMatch(setup);
    }, VERSUS_FALLBACK_MS);
  }

  /** Results screen REMATCH button. */
  rematch(): void {
    const h = this.active;
    if (!h) {
      this.ui.toast('The match is over — find a new opponent in the lobby', 'info');
      return;
    }
    h.rematch(true);
  }

  /** Leaving the results screen ends a pending rematch offer. */
  declineRematch(): void {
    const h = this.active;
    this.declinedLocally = true;
    if (h && this.resultSeen) h.rematch(false);
  }

  dispose(): void {
    if (this.fallback !== null) clearTimeout(this.fallback);
    for (const u of [...this.unsubs.splice(0), ...this.handleUnsubs.splice(0)]) u();
    this.handles.clear();
  }
}
